import { useState, useEffect, forwardRef } from "react";
import { Input, type InputProps } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface MoneyInputProps extends Omit<InputProps, "onChange" | "value" | "defaultValue"> {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  onValueChange?: (value: number) => void;
  prefix?: string;
  locale?: string;
  allowNegative?: boolean;
}

export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onValueChange,
      className,
      prefix = "R$",
      locale = "pt-BR",
      allowNegative = false,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const [displayValue, setDisplayValue] = useState<string>("");

    useEffect(() => {
      const initialValue = value ?? defaultValue;
      if (typeof initialValue === "number" && !isNaN(initialValue)) {
        if (initialValue === 0) {
          setDisplayValue("");
        } else {
          setDisplayValue(
            new Intl.NumberFormat(locale, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(initialValue)
          );
        }
      } else {
        setDisplayValue("");
      }
    }, [value, defaultValue, locale]);

    const formatNumber = (num: number) =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);

    const handleChange = (raw: string) => {
      const cleaned = raw.replace(/[\s]/g, "");
      const sign = allowNegative && cleaned.startsWith("-") ? -1 : 1;
      const digits = cleaned.replace(/[^0-9]/g, "");

      if (!digits) {
        setDisplayValue("");
        onChange?.(0);
        onValueChange?.(0);
        return;
      }

      const numericValue = (parseInt(digits, 10) / 100) * sign;
      const formatted = formatNumber(numericValue);

      setDisplayValue(formatted);
      onChange?.(numericValue);
      onValueChange?.(numericValue);
    };

    return (
      <div className="relative">
        {prefix && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground leading-none">
            {prefix}
          </span>
        )}
        <Input
          ref={ref}
          inputMode="numeric"
          className={cn(prefix ? "pl-10" : undefined, className)}
          value={displayValue}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={(event) => {
            if (!event.target.value) {
              setDisplayValue("");
            }
            onBlur?.(event);
          }}
          {...rest}
        />
      </div>
    );
  }
);

MoneyInput.displayName = "MoneyInput";
